'use client'

/**
 * AnimatedLogo Component
 * 
 * The signature Healthfluence logo with animated concentric ripple rings
 * and a breathing center "H" element.
 */

import { useCallback } from 'react'

const SIZE_PRESETS = {
  xs: 32,
  sm: 48,
  md: 80,
  lg: 120,
  xl: 160,
  '2xl': 200
}

const COLOR_VARIANTS = {
  default: {
    center: '#0D9488',
    rings: '#14B8A6',
    text: '#FFFFFF'
  },
  light: {
    center: '#FFFFFF',
    rings: 'rgba(255, 255, 255, 0.5)',
    text: '#0D9488'
  },
  dark: {
    center: '#134E4A',
    rings: '#0F766E',
    text: '#FFFFFF'
  }
}

export default function AnimatedLogo({
  size = 'md',
  variant = 'default',
  animated = true,
  showText = false,
  className = '',
  onClick,
  ...props
}) {
  const sizeInPx = typeof size === 'number' ? size : SIZE_PRESETS[size] || SIZE_PRESETS.md
  const colors = COLOR_VARIANTS[variant] || COLOR_VARIANTS.default

  const centerSize = sizeInPx * 0.42
  const fontSize = sizeInPx * 0.2
  const ringWidths = [1, 0.79, 0.58].map(ratio => sizeInPx * ratio)
  const borderWidth = Math.max(1.5, sizeInPx * 0.02)

  const ringAnimations = [
    { name: 'hf-ring-1', delay: '0s' },
    { name: 'hf-ring-2', delay: '0.5s' },
    { name: 'hf-ring-3', delay: '1s' }
  ]

  const handleKeyDown = useCallback((e) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onClick(e)
    }
  }, [onClick])

  return (
    <div
      className={`hf-logo-container ${className}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : 'img'}
      aria-label="Healthfluence logo"
      tabIndex={onClick ? 0 : undefined}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: sizeInPx,
        height: sizeInPx,
        cursor: onClick ? 'pointer' : 'default'
      }}
      {...props}
    >
      {/* Ripple Rings */}
      {ringWidths.map((ringSize, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            width: ringSize,
            height: ringSize,
            borderRadius: '50%',
            border: `${borderWidth}px solid ${colors.rings}`,
            opacity: 0.3 - index * 0.08,
            animation: animated
              ? `${ringAnimations[index].name} 3s ease-in-out infinite ${ringAnimations[index].delay}`
              : 'none',
            pointerEvents: 'none'
          }}
        />
      ))}

      {/* Center Circle */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: centerSize,
          height: centerSize,
          borderRadius: '50%',
          backgroundColor: colors.center,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: fontSize,
          fontWeight: 700,
          color: colors.text,
          fontFamily: 'Inter, system-ui, sans-serif',
          animation: animated ? 'hf-breathe 4s ease-in-out infinite' : 'none',
          boxShadow: variant === 'default' ? '0 4px 12px rgba(13, 148, 136, 0.3)' : 'none'
        }}
      >
        H
      </div>

      {/* Optional wordmark */}
      {showText && (
        <span
          style={{
            position: 'absolute',
            left: '100%',
            marginLeft: sizeInPx * 0.15,
            fontSize: sizeInPx * 0.25,
            fontWeight: 600,
            fontFamily: 'Inter, system-ui, sans-serif',
            background: 'linear-gradient(135deg, #0D9488 0%, #1E4D78 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            whiteSpace: 'nowrap'
          }}
        >
          Healthfluence
        </span>
      )}
    </div>
  )
}
